import { API, type AppAuthOptions } from '~/data/api'
import type { UserProfile } from '~/types/user'
import type { TripPrivacy, TripData } from '~/types/trip'
import type { BikeData } from '~/types/bikes'
import type { UserAchievement } from '~/types/achievements'
import type { SectionData } from '~/types/sections'
import type { GroupData } from '~/types/groups'
import type { PaginationResponse } from '~/types/api'
import type { DistanceUnit } from '~/types/types'
import {
  useSessionStorageService,
  SESSION_STORAGE_KEYS
} from '~/composables/useSessionStorageService'
import { useLocalStorageService } from '~/composables/useLocalStorageService'

export const useUserService = () => {
  const interactionStore = useInteractionStore()
  const sessionStorage = useSessionStorageService()
  const localStorage = useLocalStorageService()

  sessionStorage?.setItem(SESSION_STORAGE_KEYS.usersCache, '{}')

  function getUserFromCache(id: string | number): UserProfile | null {
    const users = sessionStorage?.getItem(SESSION_STORAGE_KEYS.usersCache)
    if (users != null) {
      const usersRecord = JSON.parse(users)
      return usersRecord[id]
    }
    return null
  }

  function setUser(user: UserProfile) {
    const users = sessionStorage?.getItem(SESSION_STORAGE_KEYS.usersCache)
    const usersRecord = users ? JSON.parse(users) : {}
    usersRecord[user.id] = user
    sessionStorage?.setItem(SESSION_STORAGE_KEYS.usersCache, JSON.stringify(usersRecord))
  }

  function setUsers(users: UserProfile[]) {
    const cachedUsers = sessionStorage?.getItem(SESSION_STORAGE_KEYS.usersCache)
    const usersRecord = cachedUsers ? JSON.parse(cachedUsers) : {}
    const newUsers = {
      ...usersRecord,
      ...users.reduce((obj: Record<string, UserProfile>, user: UserProfile) => {
        obj[user.id] = user
        return obj
      }, {})
    }
    sessionStorage?.setItem(SESSION_STORAGE_KEYS.usersCache, JSON.stringify(newUsers))
  }

  async function getUser(id: string | number, options?: AppAuthOptions): Promise<UserProfile> {
    const cachedUser = getUserFromCache(id)
    if (cachedUser != null) {
      return await new Promise((resolve) => {
        resolve(cachedUser)
      })
    }

    const response = await API?.getUser(id, options)

    if (response?.data) {
      setUser(response?.data)
    }

    // TODO this cast is not safe, user could be null, keeping it for compatibility for now
    return response?.data as UserProfile
  }

  async function getUserFresh(id: string | number): Promise<UserProfile | null> {
    const response = await API?.getUser(id)
    return response?.data || null
  }

  async function setCoverPhoto(userID: string, data: FormData): Promise<void> {
    const response = await API?.putCoverPhoto(userID, data)
    return response?.data
  }

  async function setProfilePhoto(userID: string, data: FormData): Promise<void> {
    const response = await API?.putProfilePhoto(userID, data)
    return response?.data
  }

  function getUserSettingsFromCache(userID: string | number) {
    const settings = localStorage.getItem('settings_' + userID) || '{}'
    return JSON.parse(settings)
  }

  function updateUserSettings(userID: string, newSettings: Record<string, any>) {
    const settings = localStorage.getItem('settings_' + userID) || '{}'
    const settingsJSON = JSON.parse(settings)
    const updatedSettings = { ...settingsJSON, ...newSettings }
    localStorage.setItem('settings_' + userID, JSON.stringify(updatedSettings))
  }

  async function deleteUser(userID: string) {
    const response = await API?.deleteUser(userID)
    return response?.data
  }

  async function getUserTrips(userID: number | string, privacy?: TripPrivacy) {
    const response = await API?.getUserTrips(userID, privacy)

    if (response?.data) {
      response.data.forEach((trip) => {
        interactionStore.initItem('trips', trip.id, {
          likedByMe: trip.liked_by_me,
          likeCount: trip.likes_count,
          commentCount: trip.comments_count
        })
      })
    }

    return response.data as TripData[]
  }

  async function getUserProfileInfo(userId: number) {
    const response = await API?.getUserProfileInfo(userId)
    return response?.data
  }

  async function getUserStats(userID: number, from?: Date, to?: Date) {
    const response = await API?.getUserStats(userID, from, to)

    return response?.data
  }

  async function getUserCountryRanking(from?: Date, to?: Date) {
    const response = await API?.getUserCountryRanking(from, to)

    return response?.data
  }

  async function getUserFollowedRanking(from?: Date, to?: Date) {
    const response = await API?.getUserFollowedRanking(from, to)

    return response?.data
  }

  async function getNotifications(userID: string) {
    const response = await API?.getUserNotifications(userID)
    return response?.data
  }

  async function getUserBikes(userID: number) {
    const response = await API?.getUserBikes(userID)

    if (response?.data) {
      response.data.forEach((bike) => {
        interactionStore.initItem('bikes', bike.id, {
          likedByMe: bike.liked_by_me,
          likeCount: bike.likes_count,
          commentCount: bike.comments_count
        })
      })
    }

    return response.data as BikeData[]
  }

  async function getUserHelmets(userID: number) {
    const response = await API?.getUserHelmets(userID)

    if (response?.data) {
      response.data.forEach((helmet) => {
        interactionStore.initItem('helmets', helmet.id, {
          likedByMe: helmet.liked_by_me,
          likeCount: helmet.likes_count,
          commentCount: helmet.comments_count
        })
      })
    }

    return response.data
  }

  async function getUserIntercoms(userID: number) {
    const response = await API?.getUserIntercoms(userID)

    if (response?.data) {
      response.data.forEach((intercom) => {
        interactionStore.initItem('intercoms', intercom.id, {
          likedByMe: intercom.liked_by_me,
          likeCount: intercom.likes_count,
          commentCount: intercom.comments_count
        })
      })
    }

    return response.data
  }

  async function getUserSections(userID: number) {
    const response = await API?.getUserSections(userID)

    if (response?.data) {
      response.data.forEach((section) => {
        interactionStore.initItem('sections', section.id, {
          likedByMe: section.liked_by_me,
          likeCount: section.likes_count,
          commentCount: section.comments_count
        })
      })
    }
    return response.data as SectionData[]
  }

  async function getUserAchievements(userID: string) {
    const response = await API?.getUserAchievements(userID)
    return response?.data as UserAchievement[]
  }

  async function getUserGetaways(userID: number) {
    const response = await API?.getUserGetaways(userID)
    return response.data
  }

  async function getUserRoadbookSections(userID: number) {
    const response = await API?.getUserRoadbookSections(userID)
    return response.data as SectionData[]
  }

  async function updateUser(
    id: string | number,
    data: {
      note?: string
      youtube_url?: string
      facebook_url?: string
      instagram_url?: string
      website_url?: string
      homebase?: string
      password?: string
      auto_accept_follow_requests?: boolean
      ai_image_consent?: boolean
      unit?: DistanceUnit
    }
  ): Promise<UserProfile> {
    const response = await API?.putUser(id, data)
    return response?.data as UserProfile
  }

  async function updateDistanceUnit(unit: DistanceUnit): Promise<boolean> {
    const authStore = useAuthStore()
    const mainStore = useMainStore()
    const userId = authStore.currentUser?.id

    if (!userId) {
      return false
    }

    const previousUnit = mainStore.settings?.distance ?? 'metric'

    mainStore.updateSettings({ distance: unit })

    const userData = await authStore.awaitAndSetLatestUserData(updateUser(userId, { unit }))

    if (!userData) {
      mainStore.updateSettings({ distance: previousUnit })

      return false
    }

    setUser(userData)

    return true
  }

  async function syncDistanceUnit(userData: UserProfile): Promise<void> {
    const mainStore = useMainStore()

    if (userData.unit) {
      mainStore.updateSettings({ distance: userData.unit })

      return
    }

    // The unit used to live in localStorage only. Users who picked one back then
    // have nothing stored server-side yet, so hand their choice over once instead
    // of silently resetting them to metric.
    const cachedUnit: DistanceUnit | undefined = import.meta.client
      ? getUserSettingsFromCache(userData.id)?.distance
      : undefined

    if (!cachedUnit) {
      return
    }

    await updateDistanceUnit(cachedUnit)
  }

  async function getSeasonGoal() {
    const response = await API?.getSeasonGoal()

    if (!response?.data || !response?.data[0]) return null

    return response.data.pop()
  }

  async function createSeasonGoal(data: {
    start_date: Date
    end_date: Date
    distance_goal: number
  }) {
    return await API?.createSeasonGoal(data)
  }

  async function updateSeasonGoal(
    goalID: number,
    data: { start_date: Date; end_date: Date; distance_goal: number }
  ) {
    return await API?.putSeasonGoal(goalID, data)
  }

  async function getRoutes(userId: number) {
    const response = await API?.getRoutes(userId)

    return response?.data
  }

  async function getRoute(userID: number, routeID: string | number) {
    const response = await API?.getRoute(userID, routeID)

    return response?.data
  }

  async function saveRoute(
    userID: number,
    title: string,
    distance: number,
    duration: number,
    waypoints: { position: number[]; name?: string }[],
    weighting: string,
    tags: [],
    avoidMotorways: boolean = false,
    avoidTolls: boolean = false
  ) {
    const exclude: string[] = []

    if (avoidMotorways) {
      exclude.push('motorway')
    }

    if (avoidTolls) {
      exclude.push('toll')
    }

    const response = await API?.createRoute(
      userID,
      title,
      distance,
      duration,
      waypoints,
      weighting,
      tags,
      exclude
    )

    return response?.data
  }

  async function updateRoute(
    userId: number,
    routeId: string,
    title: string,
    distance: number,
    duration: number,
    waypoints: { position: number[]; name?: string }[],
    weighting: string,
    tags: [],
    avoidMotorways: boolean = false,
    avoidTolls: boolean = false
  ) {
    const exclude: string[] = []

    if (avoidMotorways) {
      exclude.push('motorway')
    }

    if (avoidTolls) {
      exclude.push('toll')
    }

    const response = await API?.putRoute(
      userId,
      routeId,
      title,
      distance,
      duration,
      waypoints,
      weighting,
      tags,
      exclude
    )

    return response?.data
  }

  async function deleteRoute(userId: number, routeId: string) {
    const response = await API?.deleteRoute(userId, routeId)

    return response?.data
  }

  async function getSuggestedFollows(userId: number) {
    const response = await API?.getSuggestedFollows(userId)

    const data = response?.data

    if (data) {
      data.forEach((user: UserProfile) => {
        setUser(user)
      })
    }

    return data
  }

  async function getFollowers(
    userID: number,
    searchTerm?: string,
    limit?: number,
    after?: string
  ): Promise<PaginationResponse<UserProfile> | null> {
    const response = await API?.getFollowers(userID, searchTerm, limit, after)

    return response?.data ?? null
  }

  async function getFollowed(
    userID: number,
    searchTerm?: string,
    limit?: number,
    after?: string
  ): Promise<PaginationResponse<UserProfile> | null> {
    const response = await API?.getFollowed(userID, searchTerm, limit, after)

    return response?.data ?? null
  }

  async function getFollowRequests(
    userID: number,
    searchTerm?: string,
    limit?: number,
    after?: string
  ): Promise<PaginationResponse<UserProfile> | null> {
    const response = await API?.getFollowRequests(userID, searchTerm, limit, after)

    return response?.data ?? null
  }

  async function getBlockedUsers(
    userID: number,
    searchTerm?: string,
    limit?: number,
    after?: string
  ): Promise<PaginationResponse<UserProfile> | null> {
    const response = await API?.getBlockedUsers(userID, searchTerm, limit, after)

    return response?.data ?? null
  }

  async function acceptFollowRequest(userId: number) {
    const response = await API?.acceptFollowRequest(userId)
    return response?.data
  }

  async function rejectFollowRequest(userId: number) {
    const response = await API?.rejectFollowRequest(userId)
    return response?.data
  }

  async function follow(userID: number) {
    const response = await API?.follow(userID)
    return response?.data
  }

  async function unFollow(userID: number) {
    const response = await API?.unFollow(userID)
    return response?.data
  }

  async function removeFollower(followerID: number) {
    const response = await API?.removeFollower(followerID)
    return response?.data
  }

  async function blockUser(userId: number) {
    const response = await API?.blockUser(userId)
    return response?.data
  }

  async function unBlockUser(userId: number) {
    const response = await API?.unBlockUser(userId)
    return response?.data
  }

  async function consumeVoucher(voucher: string, referrer?: string) {
    const response = await API?.consumeVoucher(voucher, referrer)
    return response.data
  }

  async function getTermsOfService(locale: string) {
    const response = await API.getTermsOfService(locale)
    return response.data
  }

  async function acceptTermsofService() {
    const response = await API.acceptTermsOfService()
    return response.data
  }

  async function getGroupInvites(
    userID: string | number,
    limit?: number,
    after?: string
  ): Promise<PaginationResponse<GroupData> | null> {
    const response = await API?.getUserGroupInvites(userID, limit, after)
    return response?.data ?? null
  }

  async function getGroupRequests(userID: string | number) {
    const response = await API?.getUserGroupRequests(userID)
    return response?.data
  }

  async function getGroups(userID: string | number) {
    const response = await API?.getUserGroups(userID)
    return response?.data
  }

  return {
    getUserFromCache,
    setUser,
    setUsers,
    getUser,
    getUserFresh,
    setCoverPhoto,
    setProfilePhoto,
    getUserSettingsFromCache,
    updateUserSettings,
    deleteUser,
    getUserTrips,
    getUserProfileInfo,
    getUserStats,
    getUserCountryRanking,
    getUserFollowedRanking,
    getNotifications,
    getUserBikes,
    getUserHelmets,
    getUserIntercoms,
    getUserSections,
    getUserAchievements,
    getUserGetaways,
    getUserRoadbookSections,
    updateUser,
    updateDistanceUnit,
    syncDistanceUnit,
    getSeasonGoal,
    createSeasonGoal,
    updateSeasonGoal,
    getRoutes,
    getRoute,
    saveRoute,
    updateRoute,
    deleteRoute,
    getSuggestedFollows,
    getFollowers,
    getFollowed,
    getFollowRequests,
    getBlockedUsers,
    acceptFollowRequest,
    rejectFollowRequest,
    follow,
    unFollow,
    removeFollower,
    blockUser,
    unBlockUser,
    consumeVoucher,
    getTermsOfService,
    acceptTermsofService,
    getGroupInvites,
    getGroupRequests,
    getGroups
  }
}
